import { FC } from 'react';
import { Box, Card, Chip, Typography, IconButton, CardContent } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import Iconify from '@components/media/iconify';
import { IOrderDocument, ORDER_DOCUMENT_TYPES } from '@/types';
import PDFViewer from '@/components/media/preview-documents/preview-documents';
import DownloadButton from '../documents/salesOrder-document-download';
import { getExtension } from '@/utils/helpers/common';
import { format } from 'date-fns';
import moment from 'moment';

interface OrderOVInformationProps {
  documents?: IOrderDocument[];
  onDeleteDocument?: (document: IOrderDocument) => void;
}

/**
 * Component to display purchase order documents information
 * Shows document details in a clean card format
 */
export const OrderOVInformation: FC<OrderOVInformationProps> = ({
  documents = [],
  onDeleteDocument,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const formatDocumentType = (type: string) => {
    switch (type) {
      case ORDER_DOCUMENT_TYPES.PURCHASE_ORDER:
        return t('Purchase Order');
      case ORDER_DOCUMENT_TYPES.DELIVERY_SHEET:
        return t('Delivery Sheet (HES)');
      case ORDER_DOCUMENT_TYPES.EMAIL_APPROVAL:
        return t('Email Approval');
      case ORDER_DOCUMENT_TYPES.OTHER:
        return t('Other');
      default:
        return t('Document Type Unknown');
    }
  };

  const formatDate = (dateString: string | Date) => {
    if (!dateString) return '';
    try {
      const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
      return moment(date).utc().format('L');
    } catch {
      return typeof dateString === 'string' ? dateString : '';
    }
  };

  if (documents.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        <Iconify icon="solar:document-text-bold-duotone" sx={{ mr: 1, color: 'primary.main' }} />
        {t('Attached Documents')}
      </Typography>

      {documents.map((document) => (
        <Card
          key={document._id}
          sx={{
            mb: 2,
            border: `1px solid ${theme.palette.divider}`,
            '&:hover': {
              boxShadow: theme.shadows[2],
            },
          }}
        >
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box
              sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}
            >
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {document.file?.name || t('Document')}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Chip
                    label={formatDocumentType(document.documentType)}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20, mr: 1 }}
                  />

                  {document.documentDate && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>{formatDate(document.documentDate)}</strong>
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                {document.file?.url && (
                  <DownloadButton
                    fileUrl={document.file.url}
                    fileName={document.file.name || `document.${getExtension(document.file.url)}`}
                    icon={'material-symbols:download'}
                    color="default"
                  />
                )}

                {document.file?.url && (
                  <PDFViewer
                    file={document.file.url}
                    fileName={document.file.name || ''}
                    fileSize={document.file.size || 0}
                  />
                )}

                {onDeleteDocument && (
                  <IconButton
                    size="small"
                    onClick={() => onDeleteDocument(document)}
                    color="error"
                    title={t('Delete')}
                  >
                    <Iconify icon="solar:trash-bin-trash-bold" width={16} />
                  </IconButton>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};
